import { z } from 'zod'
import { AssetDeliveryStatus, AssetEscalationType, AssetType } from '@/features/assets/constants'
import { MeterBillingType } from '@/api/assets/constants'
import { ExportPowerEscalationType } from '@/api/billing/constants'
import { mainCurrency, precision } from '@/helpers/validation'
import { InvoiceDataSourceType } from '@/features/invoices'
import { objectWithKeys } from '@/helpers'

export const paginatedAssetSchema = z.object({
  assetCode: z.string(),
  projectCode: z.number().int(),
  encryptedProjectCode: z.string(),
  name: z.string(),
  owner: z.string(),
  type: z.nativeEnum(AssetType),
  status: z.nativeEnum(AssetDeliveryStatus),
  systemSize: z.number().nullable()
})

export function buildUpdateAssetSettingsSchema(
  isExportingPower: boolean,
  isAutoApproving: boolean,
  isWheelingAsset: boolean
) {
  const sharedSchema = z.object({
    contacts: z.array(z.any()), // For force filling the errors with contacts in the save handler.
    billingSettings: z.object({
      customerTag: z.string().min(1),
      meterBillingType: z.nativeEnum(MeterBillingType),
      gridMeterBillingType: z.nativeEnum(MeterBillingType),
      commissioningDate: z.coerce.date().nullable(),
      paymentDueInDays: z.number().int().gt(0),
      autoApprove: z.boolean(),
      gridExportValidation: isAutoApproving ? z.boolean() : z.boolean().optional(), // TODO Use superRefine to remove reliance on outside value.
      expectedEnergyVariance: isAutoApproving ? z.number().int() : z.number().int().optional(), // TODO Use superRefine to remove reliance on outside value.
      invoiceDateFormat: z.string(),
      billingCycleId: z.number(),
      enableMonthlySolarReport: z.boolean(),
      invoiceStartDate: z.coerce.date().nullable()
    }),
    utilityProvider: z.object({
      utilityProviderId: z.number().int().gt(0),
      tariffStructureId: z.number().int().gt(0),
      blendedTariff: z.number().gt(0)
    }),
    contractTerms: z.object({
      fundTransferDate: z.coerce.date(),
      contractEffectiveDate: z.coerce.date(),
      firstEscalationMonth:!isWheelingAsset ? z.number().int().gte(1).lte(12) : z.number().nullish(),
      firstEscalationYear: !isWheelingAsset ? z
        .number()
        .int()
        .gte(new Date().getFullYear() - 6)
        .lte(new Date().getFullYear() + 10) :
        z.number().nullish(),
      contractTypeId: z.number().gt(0).nullish(),
      portfolioTypeId: z.number().gt(0).nullish(),
      portfolioWeight: z.number().gt(0).nullish(),
      predictedSellingPrice: z.number().gt(0).nullish(),
      finalSellingPrice: z.number().gt(0).nullish(),
      assetDrawerNumber: z.number().nullish(),
      performanceRatio: z.number().gte(0).nullish(),
      latePaymentInterest: z.number().gte(0).nullish(),
      specialConditions: z.string().nullish(),
      signedAgreementLink: z.string().url().nullish(),
      expectedIrradiance: z.number().nullish(),
      periodMonthsWAAE: z.number().nullish(),
      environmentalAttributeOwnershipId: z.number().nullish(),
      amaWarrantyPeriodEscalation: z.number().nullish(),
      amaGuaranteePeriodEscalation: z.number().nullish(),
      practicalCompletionDate: z.coerce.date().nullish(),
      roofRental: z.number().nullish(),
      roofRentalEscalation: z.number().nullish(),
      projectBudget: z.number().nullish(),
      projectCost: z.number().nullish(),
      isOwnedByClient: z.boolean().nullish(),
      isMaintenanceSLA: z.boolean().nullish(),
      fixedLeaseStartDate: z.coerce.date().nullish(),
      annualEscalationType: !isWheelingAsset ? z.nativeEnum(AssetEscalationType).nullable() : z.nativeEnum(AssetEscalationType).nullish(),
      annualEscalationPercentage: !isWheelingAsset ? z.number({ required_error: 'Annual escalation percentage is required' })
        .nullable()
        .refine(v => v === null || Number.isFinite(v), 'Annual escalation percentage is required') :
        z.number().nullish()
    }),
    invoiceDetails: z.object({
      contractFundEntityId: z.number(),
      customerExternalIdentifier: z.string()
    }),
    tariffProfile: z.object({
      escalationDate: isExportingPower ? z.date() : z.date().optional(), // TODO Use superRefine to remove reliance on outside value.
      escalationValue: isExportingPower ? z.number() : z.number().optional(), // TODO Use superRefine to remove reliance on outside value.
      escalationType: isExportingPower
        ? z.nativeEnum(ExportPowerEscalationType)
        : z.nativeEnum(ExportPowerEscalationType).optional() // TODO Use superRefine to remove reliance on outside value.
    })
  })

  return z.discriminatedUnion('assetType', [
    z.object({
      assetType: z.literal(AssetType.Solar),
      ...sharedSchema.shape,
      tariffProfile: z.object({
        ...sharedSchema.shape.tariffProfile.shape,
        tariffStructureId: z.number().int().gt(0),
        // Rate fields are named `rates[<tariffTypeId>]`, so vee-validate rebuilds `rates` as a sparse array
        // whenever the container is recreated (e.g. when the rates table re-mounts). Normalise back to a record.
        rates: z.preprocess(
          v => Array.isArray(v) ? Object.fromEntries(Object.entries(v)) : v,
          z.record(
            z.string(),
            z.object({
              pvRate: mainCurrency(z.number()),
              exportRate: isExportingPower
                ? mainCurrency(z.number())
                : mainCurrency(z.number()).optional() // TODO Use superRefine to remove reliance on outside value.
            })
          )
          .refine(rates => Object.keys(rates).length > 0, 'At least one rate is required.'))
      })
    }),
    z.object({
      assetType: z.literal(AssetType.Wheeling),
      ...sharedSchema.shape,
      billingSettings: z.object({
        ...sharedSchema.shape.billingSettings.shape,
        invoiceDataSourceType: z.nativeEnum(InvoiceDataSourceType)
      }),
      contractTerms: z.object({
        ...sharedSchema.shape.contractTerms.shape,
        vppaTypeId: z.number().gte(0).nullable(),
        vppaSystemSizeSoldInKwp: z.number().gte(0).nullable(),
        vppaSystemSizeFundInKwp: z.number().gte(0).nullable(),
        vppaSystemSizeAsBuiltInKwp: z.number().gte(0).nullable(),
        initialContractTermInYears: z.number().int().gte(0).nullable(),
        fullContractTermInYears: z.number().int().gte(0).nullable(),
        renewableEnergyCertificatesEnabled: z.boolean().nullable(),
        renewableEnergyCertificatesIssuanceFeeInDollars: precision(z.number().gte(0), 7).nullish(),
        renewableEnergyCertificatesTransferFeeInRands: precision(z.number().gte(0), 7).nullish(),
        renewableEnergyCertificatesPlatformFeeInRands: precision(z.number().gte(0), 7).nullish(),
        renewableEnergyCertificatesMarkupPercentage: precision(z.number().gte(0), 4).nullish(),
        renewableEnergyCertificatesEffectiveDate: z.coerce.date().nullish(),
      }).superRefine(
        (arg, ctx) => {
          if (!arg.renewableEnergyCertificatesEnabled) {
            return;
          }

          const propertiesToCheck = objectWithKeys(arg, [
            'renewableEnergyCertificatesIssuanceFeeInDollars',
            'renewableEnergyCertificatesTransferFeeInRands',
            'renewableEnergyCertificatesPlatformFeeInRands',
            'renewableEnergyCertificatesMarkupPercentage',
            'renewableEnergyCertificatesEffectiveDate',
          ])

          for (const pair of Object.entries(propertiesToCheck)) {
            const [key, value] = pair
            if (value === null || value === undefined) {
              ctx.addIssue({
                code: 'custom',
                message: 'Value required',
                path: [key]
              })
            }
          }
        }
      )
    })
  ])
}

export const createAssetSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(AssetType.Solar),
    unifiiProjectCode: z.number(),
    productId: z.number(),
    ownerId: z.number(),
    systemSize: z.number().gt(0),
    consumptionYield: z.number().gt(0),
    escalationType: z.nativeEnum(AssetEscalationType),
    escalationPercentage: z.number().min(0).max(100),
    vcomSystemKey: z.string().trim().length(6, 'VCOM system key must be exactly 6 characters.')
  }),
  z.object({
    type: z.literal(AssetType.Wheeling),
    wheelingExternalKey: z.string(),
    unifiiProjectCode: z.number(),
    ownerId: z.number()
  })
])

export type UpdateAssetSettings = z.output<ReturnType<typeof buildUpdateAssetSettingsSchema>>
